import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import dayjs from 'dayjs';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { RightAlignInputField } from '../style/textfield';
import { TypographyNomalBold, TypographySmall } from '../style/typography';
import InspectionPeriod from '../components/inspection_period';
import InspectionDate from '../components/inspection_date';
import { validationSchemaCheckSumary } from '../func/validation_check';
import { CustomTextFieldElementCheckSummary } from '../components/CustomTextFieldElement';

const INPUT_STYLE = {
    "& .MuiInputBase-root": {
        width: '260%',
        height: 'auto',
    },
};

export default function CheckSummary({ 
    checkSummary, 
    setCheckSummary, 
    isReadOnly
}) {
    const { control, setValue, formState: { errors }, trigger } = useForm({
        defaultValues: { checkSummarys: checkSummary },
        resolver: yupResolver(validationSchemaCheckSumary),
        mode: "onBlur"
    });

    useEffect(() => {
        trigger("checkSummarys.inspector");
    }, [trigger]);

    const updateCheckSummary = (key, value) => {
        setCheckSummary(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setCheckSummary(prev => ({
            ...prev,
            [field]: value
        }));
        // React Hook Formの値も更新
        setValue(`checkSummarys.${field}`, value);
    };

    const renderFormField = (name, label) => (
        <CustomTextFieldElementCheckSummary
            name={name}
            control={control}
            label={label}
            disabled={false}
            onChange={handleChange(name)}
            errors={errors}
            sx={INPUT_STYLE}
        />
    );

    const renderAccelerationField = (fieldName, label, type) => (
        <Grid size={{ xs: 12, sm: 2, md: 2 }}>
            {isReadOnly ? (
                <span style={type !== 'z' ? { marginRight: "8px" } : {}}>
                    {checkSummary?.[fieldName] || "—"}
                </span>
            ) : (
                <RightAlignInputField
                    label={label}
                    value={checkSummary?.[fieldName] || ""}
                    onChange={handleChange(fieldName)}
                    disabled={true}
                />
            )}
        </Grid>
    );

    const renderAccelerationSection = (title, prefix) => (
        <>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TypographyNomalBold>{title}</TypographyNomalBold>
                <TypographySmall>※地震計を設置した場合はシステム担当者に連絡してください</TypographySmall>
            </Grid>
            {renderAccelerationField(`${prefix}_x`, "X", "x")}
            {renderAccelerationField(`${prefix}_y`, "Y", "y")}
            {renderAccelerationField(`${prefix}_z`, "Z", "z")}
            <Grid size={{ xs: 12, sm: 2, md: 3 }}>
                <></>
            </Grid>
        </>
    );

    return (
        <Grid container
            spacing={{ xs: 1, sm: 2 }}
            columns={12}
            p={{ xs: 1, sm: 2, md: 3 }}
        >
            {/* 点検年月日 */}
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TypographyNomalBold>点検年月日</TypographyNomalBold>
            </Grid>
            <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                {isReadOnly ? (
                    checkSummary?.inspection_date
                        ? dayjs(checkSummary.inspection_date).format("YYYY年M月D日")
                        : "—"
                ) : (
                    <InspectionDate
                        label="点検年月日を選択"
                        field="inspection_date"
                        checkSummary={checkSummary}
                        updateCheckSummary={updateCheckSummary}
                        handleChange={handleChange("inspection_date")}
                    />
                )}
            </Grid>

            {/* 点検期間 */}
            <Grid size={{ xs: 12 }}>
                <InspectionPeriod
                    checkSummary={checkSummary}
                    updateCheckSummary={updateCheckSummary}
                    handleChange={handleChange}
                    isReadOnly={isReadOnly}
                />
            </Grid>

            {/* 前回点検年月日 */}
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TypographyNomalBold>前回点検年月日</TypographyNomalBold>
            </Grid>
            <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                {isReadOnly ? (
                    checkSummary?.previous_at
                        ? dayjs(checkSummary.previous_at).format("YYYY年M月D日")
                        : "—"
                ) : (
                    <InspectionDate
                        label="前回点検年月日を選択"
                        field="previous_at"
                        checkSummary={checkSummary}
                        updateCheckSummary={updateCheckSummary}
                        handleChange={handleChange("previous_at")}
                    />
                )}
            </Grid>

            {/* 点検者 */}
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TypographyNomalBold>点検者</TypographyNomalBold>
            </Grid>
            <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                {isReadOnly ? (
                    checkSummary?.inspector || "—"
                ) : (
                    renderFormField("inspector", "記入例)山田太郎/山田花子")
                )}
            </Grid>

            {/* 点検要因 */}
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TypographyNomalBold>点検要因</TypographyNomalBold>
            </Grid>
            <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                {isReadOnly ? (
                    checkSummary?.reason || "—"
                ) : (
                    renderFormField("reason", "点検要因")
                )}
            </Grid>

            {/* ダム地点震度 */}
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TypographyNomalBold>ダム地点震度</TypographyNomalBold>
            </Grid>
            <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                {isReadOnly ? (
                    checkSummary?.intensity || "—"
                ) : (
                    renderFormField("intensity", "ダム地点震度")
                )}
            </Grid>

            {/* 天端加速度 */}
            {renderAccelerationSection("加速度(gal)：天端(X Y Z)", "gal_top")}

            {/* 基礎加速度 */}
            {renderAccelerationSection("加速度(gal)：基礎(X Y Z)", "gal_base")}
        </Grid>
    );
}